/**
 * AccessState — render the THREE distinct list/page states unambiguously.
 *
 * Background: pages used to render a blank table whether the user was
 * (a) denied (403), (b) had a scope filter that returned 0 rows, or
 * (c) genuinely had no data. The user couldn't tell which.
 *
 * Use this component at the top of any list/table view:
 *
 *   <AccessState
 *     module="fleet"
 *     action="view"
 *     items={vehicles}
 *     denied={fleetReq.denied}
 *     loading={loading}
 *     entityLabel="vehicles"
 *     emptyAction={<Button>Add vehicle</Button>}
 *   >
 *     <Table>...</Table>
 *   </AccessState>
 *
 * It will render:
 *   - "No access to {module}" panel    →  403 OR engine says no cap
 *   - "No {entityLabel} in your assigned scope"  →  200 empty AND scope < all
 *   - genuine empty state               →  200 empty AND scope === all
 *   - children (the table itself)       →  items.length > 0
 */
import React from 'react';
import { ShieldOff, Inbox, Search } from 'lucide-react';
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions';
import { Card, CardContent } from '../ui/card';

export default function AccessState({
  module,
  action = 'view',
  items,
  denied = false,
  loading = false,
  entityLabel = 'records',
  emptyAction,
  noAccessTitle,
  noAccessDescription,
  children,
}) {
  const perms = useEffectivePermissions();

  // Engine says no cap → render the 403 state proactively, even before the
  // network round-trip. (Catching the actual 403 via `denied` is the fallback
  // for cases where the engine hasn't loaded yet or the call slipped through.)
  const engineDenied = perms.loaded && !perms.can(module, action);
  const isDenied = denied || engineDenied;

  if (loading) return children; // delegate to caller's spinner

  if (isDenied) {
    return (
      <Card data-testid={`access-state-no-access-${module}`} className="border-amber-300 bg-amber-50/40">
        <CardContent className="py-10 flex flex-col items-center text-center gap-3">
          <div className="rounded-full bg-amber-100 p-3">
            <ShieldOff className="w-6 h-6 text-amber-700" />
          </div>
          <div className="space-y-1">
            <p className="font-medium">
              {noAccessTitle || `No access to ${module.replace('_', ' ')}`}
            </p>
            <p className="text-sm text-muted-foreground max-w-md">
              {noAccessDescription || `Your role doesn't include the "${action}" capability on ${module}. Ask an administrator to grant it from the Permissions screen.`}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (items && items.length === 0) {
    const scopeLimited = perms.scope?.type && perms.scope.type !== 'all';
    if (scopeLimited) {
      return (
        <Card data-testid={`access-state-scope-empty-${module}`} className="border-blue-300 bg-blue-50/40">
          <CardContent className="py-10 flex flex-col items-center text-center gap-3">
            <div className="rounded-full bg-blue-100 p-3">
              <Search className="w-6 h-6 text-blue-700" />
            </div>
            <div className="space-y-1">
              <p className="font-medium">No {entityLabel} in your assigned scope</p>
              <p className="text-sm text-muted-foreground max-w-md">
                You&apos;re scoped to <span className="font-medium">{perms.scope.type}</span>
                {perms.scope.value ? ` (${perms.scope.value})` : ''}. There are no matching {entityLabel} for that slice. Records outside your scope are not visible.
              </p>
            </div>
          </CardContent>
        </Card>
      );
    }
    return (
      <Card data-testid={`access-state-empty-${module}`}>
        <CardContent className="py-10 flex flex-col items-center text-center gap-3">
          <div className="rounded-full bg-muted p-3">
            <Inbox className="w-6 h-6 text-muted-foreground" />
          </div>
          <div className="space-y-1">
            <p className="font-medium">No {entityLabel} yet</p>
            <p className="text-sm text-muted-foreground">Get started by creating your first record.</p>
          </div>
          {emptyAction}
        </CardContent>
      </Card>
    );
  }

  return children;
}
